/**
 * System Health — Operational observability for platform_admins.
 * Real data from /api/v2/admin/system-health: background-task queues,
 * audit-log error rollup, email delivery, DB pool, JD-normalizer queue depth.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Activity, Server, Database, Mail, Cpu, AlertTriangle, RefreshCw, Loader2, FileText, CheckCircle2 } from "lucide-react";

const cls = (...xs) => xs.filter(Boolean).join(" ");

function StatusDot({ ok, label }) {
    return (
        <div className="flex items-center gap-2" data-testid={`service-status-${label?.toLowerCase().replace(/\s+/g, "-")}`}>
            <span className={cls("w-2 h-2 rounded-full", ok ? "bg-emerald-400" : "bg-red-400")} />
            <span className="text-xs text-white">{label}</span>
            <span className="text-[10px] font-mono text-gray-500 uppercase ml-auto">{ok ? "configured" : "missing"}</span>
        </div>
    );
}

function StatCard({ label, value, sublabel, tone = "default", testId }) {
    const tones = {
        default: "border-border/40",
        success: "border-emerald-500/40",
        warn: "border-amber-500/40",
        error: "border-red-500/40",
    };
    return (
        <div className={cls("p-4 border bg-card", tones[tone])} data-testid={testId}>
            <p className="text-[10px] font-mono uppercase tracking-wider text-gray-500">{label}</p>
            <p className="text-2xl font-display font-bold text-white mt-1">{value ?? "—"}</p>
            {sublabel && <p className="text-[10px] font-mono text-gray-600 mt-0.5">{sublabel}</p>}
        </div>
    );
}

export default function AdminHealthPage() {
    const { api } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(() => {
        setLoading(true); setError("");
        api.get("/v2/admin/system-health")
            .then(r => setData(r.data))
            .catch(e => setError(e.response?.data?.detail || e.message))
            .finally(() => setLoading(false));
    }, [api]);

    useEffect(() => { load(); }, [load]);

    return (
        <div className="p-8 max-w-6xl space-y-6" data-testid="admin-health">
            <div className="flex items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-display font-bold text-white">System Health</h1>
                    <p className="text-sm text-gray-500 mt-1 font-body">
                        Background-task queues, error rates, email delivery, DB pool, service connectivity.
                    </p>
                </div>
                <button onClick={load} disabled={loading}
                    data-testid="refresh-btn"
                    className="px-3 py-2 border border-border/40 text-gray-400 hover:text-white hover:border-white/30 transition-colors inline-flex items-center gap-2 text-xs">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Refresh
                </button>
            </div>

            {error && (
                <div className="p-3 border border-red-500/40 bg-red-500/10 text-xs text-red-300">{error}</div>
            )}

            {!data ? (
                <div className="p-12 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-gray-500 mx-auto" />
                </div>
            ) : (
                <div className="grid grid-cols-12 gap-4">
                    {/* Services */}
                    <Section title="Services" icon={Server} colSpan={4}>
                        <div className="space-y-2">
                            <StatusDot ok={data.services.anthropic_emergent_llm} label="Anthropic / Emergent LLM" />
                            <StatusDot ok={data.services.resend_email} label="Resend Email" />
                            <StatusDot ok={data.services.serpapi} label="SerpAPI" />
                            <StatusDot ok={data.services.postgres} label="PostgreSQL" />
                            <StatusDot ok={data.services.mongodb_legacy} label="MongoDB (legacy)" />
                        </div>
                        <p className="text-[10px] font-mono text-gray-600 mt-3">
                            As of {new Date(data.as_of).toLocaleString()}
                        </p>
                    </Section>

                    {/* Background jobs */}
                    <Section title="Background Jobs (Scoring)" icon={Cpu} colSpan={8}>
                        <div className="grid grid-cols-4 gap-3">
                            <StatCard testId="job-success-rate" label="Success rate (24h)"
                                value={data.background_jobs.scoring_jobs_success_rate_pct == null ? "—" : `${data.background_jobs.scoring_jobs_success_rate_pct}%`}
                                sublabel={`${data.background_jobs.scoring_jobs_24h_completed} / ${data.background_jobs.scoring_jobs_24h_total}`}
                                tone={data.background_jobs.scoring_jobs_success_rate_pct >= 95 ? "success" : data.background_jobs.scoring_jobs_success_rate_pct >= 80 ? "warn" : data.background_jobs.scoring_jobs_24h_total ? "error" : "default"}
                            />
                            <StatCard testId="job-failed-24h" label="Failed (24h)" value={data.background_jobs.scoring_jobs_24h_failed}
                                tone={data.background_jobs.scoring_jobs_24h_failed > 0 ? "error" : "default"} />
                            <StatCard testId="job-p50" label="p50 latency"
                                value={data.background_jobs.p50_duration_seconds == null ? "—" : `${data.background_jobs.p50_duration_seconds}s`} />
                            <StatCard testId="job-p95" label="p95 latency"
                                value={data.background_jobs.p95_duration_seconds == null ? "—" : `${data.background_jobs.p95_duration_seconds}s`}
                                tone={data.background_jobs.p95_duration_seconds > 60 ? "warn" : "default"} />
                        </div>
                        <p className="text-[10px] font-mono text-gray-600 mt-3">
                            Status: {Object.entries(data.background_jobs.scoring_jobs).map(([k, v]) => `${k}=${v}`).join("  •  ") || "no jobs"}
                        </p>
                    </Section>

                    {/* JD Normalizer queue */}
                    <Section title="JD Normalizer Queue" icon={FileText} colSpan={6}>
                        <div className="grid grid-cols-3 gap-3">
                            <StatCard testId="jd-in-flight" label="In-flight" value={data.jd_normalizer_queue.in_flight_normalize_count}
                                tone={data.jd_normalizer_queue.in_flight_normalize_count > 0 ? "warn" : "default"} />
                            <StatCard testId="jd-pending" label="Pending review" value={data.jd_normalizer_queue.canonical_pending_review} />
                            <StatCard testId="jd-approved" label="Approved" value={data.jd_normalizer_queue.canonical_approved}
                                tone="success" />
                        </div>
                        <p className="text-[10px] font-mono text-gray-600 mt-3">
                            sources: {data.jd_normalizer_queue.source_documents_total} • canonicals: {data.jd_normalizer_queue.canonical_jds_total}
                        </p>
                    </Section>

                    {/* Email delivery */}
                    <Section title="Email Delivery (24h)" icon={Mail} colSpan={3}>
                        <div className="space-y-2">
                            <StatCard testId="email-sent" label="Sent" value={data.email_delivery_24h.sent} tone="success" />
                            <StatCard testId="email-failed" label="Failed" value={data.email_delivery_24h.failed}
                                tone={data.email_delivery_24h.failed > 0 ? "error" : "default"} />
                        </div>
                    </Section>

                    {/* DB pool */}
                    <Section title="DB Pool" icon={Database} colSpan={3}>
                        <div className="space-y-2 text-xs">
                            <KV label="Pool size" value={data.db_pool.size} />
                            <KV label="Checked out" value={data.db_pool.checked_out} />
                            <KV label="Checked in" value={data.db_pool.checked_in} />
                            <KV label="Overflow" value={data.db_pool.overflow} />
                        </div>
                    </Section>

                    {/* Error rollup */}
                    <Section title="Audit Log (24h)" icon={AlertTriangle} colSpan={6}>
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <StatCard testId="audit-total" label="Total events" value={data.audit_log_24h.total_events} />
                            <StatCard testId="audit-errors" label="Errors" value={data.audit_log_24h.error_count}
                                tone={data.audit_log_24h.error_count > 0 ? "error" : "success"} />
                        </div>
                        <div className="space-y-1 max-h-40 overflow-y-auto">
                            {Object.entries(data.audit_log_24h.by_event_type_top || {}).slice(0, 8).map(([k, v]) => (
                                <div key={k} className="flex items-center justify-between text-xs font-mono py-1 px-2 hover:bg-white/[0.02]">
                                    <span className="text-gray-400 truncate">{k}</span>
                                    <span className="text-white">{v}</span>
                                </div>
                            ))}
                        </div>
                    </Section>

                    {/* Recent errors */}
                    <Section title="Recent Errors" icon={AlertTriangle} colSpan={6}>
                        {data.audit_log_24h.recent_errors?.length ? (
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                                {data.audit_log_24h.recent_errors.map((e, i) => (
                                    <div key={i} className="border border-red-500/30 bg-red-500/5 p-2 text-xs" data-testid={`recent-error-${i}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="font-mono text-red-300">{e.event_type}</span>
                                            <span className="text-[10px] font-mono text-gray-600">{new Date(e.timestamp).toLocaleString()}</span>
                                        </div>
                                        {e.details && Object.keys(e.details).length > 0 && (
                                            <p className="text-[10px] font-mono text-gray-500 truncate">{JSON.stringify(e.details)}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-xs text-emerald-300">
                                <CheckCircle2 className="w-4 h-4" /> No errors in last 24h
                            </div>
                        )}
                    </Section>
                </div>
            )}
        </div>
    );
}

function Section({ title, icon: Icon, colSpan = 6, children }) {
    const spanCls = {
        3: "col-span-3", 4: "col-span-4", 6: "col-span-6", 8: "col-span-8", 12: "col-span-12",
    }[colSpan] || "col-span-6";
    return (
        <div className={`${spanCls} border border-border/40 bg-card p-4`} data-testid={`section-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-center gap-2 mb-3">
                <Icon className="w-4 h-4 text-gray-500" />
                <h2 className="text-sm font-display text-white">{title}</h2>
            </div>
            {children}
        </div>
    );
}

function KV({ label, value }) {
    return (
        <div className="flex items-center justify-between font-mono">
            <span className="text-gray-500">{label}</span>
            <span className="text-white">{value ?? "—"}</span>
        </div>
    );
}
